/**
 * Worker pools with bounded concurrency and explicit queueing.
 *
 * @module bquery/concurrency
 */

import { TaskWorkerAbortError, TaskWorkerError } from './errors';
import { createRpcWorker } from './rpc';
import { createTaskWorker } from './task';
import type {
  CreateRpcPoolOptions,
  CreateTaskPoolOptions,
  PoolMetrics,
  RpcPool,
  RpcWorker,
  TaskPool,
  TaskRunOptions,
  TaskWorker,
  TaskWorkerState,
  WorkerRpcHandlers,
  WorkerRpcSource,
  WorkerTaskSource,
} from './types';

const DEFAULT_POOL_CONCURRENCY = 4;
const MAX_METRICS_HISTORY = 128;

interface QueueEntry<TJob, TResult> {
  abortHandler?: () => void;
  job: TJob;
  options: TaskRunOptions;
  order: number;
  reject: (reason?: unknown) => void;
  resolve: (value: TResult | PromiseLike<TResult>) => void;
  signal?: AbortSignal;
}

interface PoolRuntime<TJob, TResult> {
  readonly state: TaskWorkerState;
  readonly busy: boolean;
  readonly concurrency: number;
  readonly pending: number;
  readonly size: number;
  readonly paused: boolean;
  readonly metrics: PoolMetrics;
  enqueue(job: TJob, options?: TaskRunOptions): Promise<TResult>;
  clear(): void;
  pause(): void;
  resume(): void;
  onIdle(): Promise<void>;
  terminate(): void;
}

interface CreatePoolRuntimeOptions<TWorker extends { busy: boolean }, TJob, TResult> {
  abortedWhileQueuedMessage: string;
  clearMessage: string;
  concurrency: number;
  createWorkers: (concurrency: number) => TWorker[];
  queueFullMessage: string;
  terminatedMessage: string;
  workerTerminatedMessage: string;
  runWorker: (worker: TWorker, job: TJob, options: TaskRunOptions) => Promise<TResult>;
  maxQueue: number;
}

const normalizeConcurrency = (concurrency: number | undefined, label: string): number => {
  if (concurrency === undefined) {
    return DEFAULT_POOL_CONCURRENCY;
  }

  if (!Number.isInteger(concurrency) || concurrency < 1) {
    throw new RangeError(`${label} concurrency must be a positive integer.`);
  }

  return concurrency;
};

const normalizeMaxQueue = (maxQueue: number | undefined, label: string): number => {
  if (maxQueue === undefined) {
    return Number.POSITIVE_INFINITY;
  }

  if (maxQueue === Number.POSITIVE_INFINITY) {
    return maxQueue;
  }

  if (!Number.isInteger(maxQueue) || maxQueue < 0) {
    throw new RangeError(`${label} maxQueue must be a non-negative integer or Infinity.`);
  }

  return maxQueue;
};

const normalizePriority = (priority: number | undefined): number => {
  if (typeof priority !== 'number' || !Number.isFinite(priority)) {
    return 0;
  }

  return priority;
};

const compareQueueEntries = <TJob, TResult>(
  left: QueueEntry<TJob, TResult>,
  right: QueueEntry<TJob, TResult>
): number =>
  normalizePriority(right.options.priority) - normalizePriority(left.options.priority) ||
  left.order - right.order;

const insertQueueEntry = <TJob, TResult>(
  queue: Array<QueueEntry<TJob, TResult>>,
  entry: QueueEntry<TJob, TResult>
): void => {
  let low = 0;
  let high = queue.length;

  while (low < high) {
    const mid = Math.floor((low + high) / 2);
    if (compareQueueEntries(entry, queue[mid]!) < 0) {
      high = mid;
    } else {
      low = mid + 1;
    }
  }

  queue.splice(low, 0, entry);
};

const cloneMetrics = (metrics: PoolMetrics): PoolMetrics => ({ ...metrics });

const detachAbortListener = <TJob, TResult>(entry: QueueEntry<TJob, TResult>): void => {
  if (entry.abortHandler && entry.signal) {
    entry.signal.removeEventListener('abort', entry.abortHandler);
  }

  entry.abortHandler = undefined;
  entry.signal = undefined;
};

const createPoolRuntime = <TWorker extends { busy: boolean }, TJob, TResult>({
  abortedWhileQueuedMessage,
  clearMessage,
  concurrency,
  createWorkers,
  queueFullMessage,
  terminatedMessage,
  workerTerminatedMessage,
  runWorker,
  maxQueue,
}: CreatePoolRuntimeOptions<TWorker, TJob, TResult>): PoolRuntime<TJob, TResult> => {
  const queue: Array<QueueEntry<TJob, TResult>> = [];
  const workers = createWorkers(concurrency);
  const metricsHistory: number[] = [];
  let metricsRuntimeTotalMs = 0;
  const metrics: PoolMetrics = {
    avgRuntimeMs: 0,
    completed: 0,
    failed: 0,
    p95RuntimeMs: 0,
  };
  let disposed = false;
  let paused = false;
  let running = 0;
  let nextOrder = 0;
  let idleWaiters: Array<{ reject: (reason?: unknown) => void; resolve: () => void }> = [];

  const resolveIdle = (): void => {
    if (running > 0 || queue.length > 0) {
      return;
    }

    const waiters = idleWaiters;
    idleWaiters = [];
    for (const waiter of waiters) {
      waiter.resolve();
    }
  };

  const rejectIdle = (error: TaskWorkerError): void => {
    const waiters = idleWaiters;
    idleWaiters = [];
    for (const waiter of waiters) {
      waiter.reject(error);
    }
  };

  const updateMetrics = (durationMs: number, failed: boolean): void => {
    if (failed) {
      metrics.failed += 1;
      return;
    }

    metrics.completed += 1;
    metricsHistory.push(durationMs);
    metricsRuntimeTotalMs += durationMs;
    if (metricsHistory.length > MAX_METRICS_HISTORY) {
      const removed = metricsHistory.shift();
      if (typeof removed === 'number') {
        metricsRuntimeTotalMs -= removed;
      }
    }
    metrics.avgRuntimeMs = metricsRuntimeTotalMs / metricsHistory.length;
    const sorted = [...metricsHistory].sort((a, b) => a - b);
    const index = Math.max(0, Math.ceil(sorted.length * 0.95) - 1);
    metrics.p95RuntimeMs = sorted[index] ?? durationMs;
  };

  const drain = (): void => {
    if (disposed || paused || queue.length === 0) {
      resolveIdle();
      return;
    }

    for (const worker of workers) {
      if (queue.length === 0) {
        return;
      }

      if (worker.busy) {
        continue;
      }

      const entry = queue.shift()!;
      if (entry.signal?.aborted) {
        detachAbortListener(entry);
        entry.reject(new TaskWorkerAbortError(abortedWhileQueuedMessage));
        resolveIdle();
        continue;
      }

      detachAbortListener(entry);
      running++;
      const startedAt = performance.now();
      void runWorker(worker, entry.job, entry.options)
        .then(
          (value) => {
            updateMetrics(performance.now() - startedAt, false);
            entry.resolve(value);
          },
          (error) => {
            updateMetrics(performance.now() - startedAt, true);
            entry.reject(error);
          }
        )
        .finally(() => {
          running--;
          drain();
          resolveIdle();
        });
    }
  };

  const rejectQueued = (error: TaskWorkerError): void => {
    const queued = queue.splice(0);
    for (const entry of queued) {
      detachAbortListener(entry);
      entry.reject(error);
    }
  };

  return {
    get state(): TaskWorkerState {
      if (disposed) {
        return 'terminated';
      }

      return running > 0 || queue.length > 0 ? 'running' : 'idle';
    },
    get busy(): boolean {
      return running > 0 || queue.length > 0;
    },
    concurrency,
    get pending(): number {
      return running;
    },
    get size(): number {
      return queue.length;
    },
    get paused(): boolean {
      return paused;
    },
    get metrics(): PoolMetrics {
      return cloneMetrics(metrics);
    },
    enqueue(job: TJob, options: TaskRunOptions = {}): Promise<TResult> {
      if (disposed) {
        return Promise.reject(new TaskWorkerError(workerTerminatedMessage, 'TERMINATED'));
      }

      if (options.signal?.aborted) {
        return Promise.reject(new TaskWorkerAbortError());
      }

      return new Promise<TResult>((resolve, reject) => {
        const entry: QueueEntry<TJob, TResult> = {
          job,
          options,
          order: nextOrder++,
          reject,
          resolve,
        };

        const idleWorker = paused ? undefined : workers.find((worker) => !worker.busy);
        if (idleWorker) {
          running++;
          const startedAt = performance.now();
          void runWorker(idleWorker, job, options)
            .then(
              (value) => {
                updateMetrics(performance.now() - startedAt, false);
                resolve(value);
              },
              (error) => {
                updateMetrics(performance.now() - startedAt, true);
                reject(error);
              }
            )
            .finally(() => {
              running--;
              drain();
              resolveIdle();
            });
          return;
        }

        if (queue.length >= maxQueue) {
          reject(new TaskWorkerError(queueFullMessage, 'QUEUE_FULL'));
          return;
        }

        if (options.signal) {
          entry.signal = options.signal;
          entry.abortHandler = () => {
            const index = queue.indexOf(entry);
            if (index === -1) {
              return;
            }

            queue.splice(index, 1);
            detachAbortListener(entry);
            reject(new TaskWorkerAbortError(abortedWhileQueuedMessage));
          };
          options.signal.addEventListener('abort', entry.abortHandler, { once: true });
        }

        insertQueueEntry(queue, entry);
      });
    },
    clear(): void {
      if (disposed || queue.length === 0) {
        return;
      }

      rejectQueued(new TaskWorkerError(clearMessage, 'QUEUE_CLEARED'));
      resolveIdle();
    },
    pause(): void {
      if (disposed) {
        return;
      }

      paused = true;
    },
    resume(): void {
      if (disposed || !paused) {
        return;
      }

      paused = false;
      drain();
    },
    onIdle(): Promise<void> {
      if (running === 0 && queue.length === 0) {
        return Promise.resolve();
      }

      if (disposed) {
        return Promise.reject(new TaskWorkerError(workerTerminatedMessage, 'TERMINATED'));
      }

      return new Promise<void>((resolve, reject) => {
        idleWaiters.push({ reject, resolve });
      });
    },
    terminate(): void {
      if (disposed) {
        return;
      }

      disposed = true;
      const terminationError = new TaskWorkerError(terminatedMessage, 'TERMINATED');
      rejectQueued(terminationError);
      rejectIdle(terminationError);
      for (const worker of workers) {
        if ('terminate' in worker && typeof worker.terminate === 'function') {
          worker.terminate();
        }
      }
    },
  };
};

const createWorkerNames = (
  name: string | undefined,
  concurrency: number
): Array<string | undefined> => {
  if (!name) {
    return Array.from({ length: concurrency }, () => undefined);
  }

  if (concurrency === 1) {
    return [name];
  }

  return Array.from({ length: concurrency }, (_, index) => `${name}-${index + 1}`);
};

/**
 * Creates a reusable pool of task workers with bounded concurrency and FIFO queueing.
 *
 * @example
 * ```ts
 * import { createTaskPool } from '@bquery/bquery/concurrency';
 *
 * const pool = createTaskPool(
 *   ({ value }: { value: number }) => value * 2,
 *   { concurrency: 4, maxQueue: 16, name: 'double-pool' }
 * );
 *
 * const results = await Promise.all([
 *   pool.run({ value: 1 }),
 *   pool.run({ value: 2 }),
 *   pool.run({ value: 3 }),
 * ]);
 *
 * pool.terminate();
 * ```
 */
export function createTaskPool<TInput = void, TResult = unknown>(
  source: WorkerTaskSource<TInput, TResult>,
  options: CreateTaskPoolOptions = {}
): TaskPool<TInput, TResult> {
  const { concurrency: concurrencyOption, maxQueue: maxQueueOption, ...workerOptions } = options;
  const concurrency = normalizeConcurrency(concurrencyOption, 'Task pool');
  const maxQueue = normalizeMaxQueue(maxQueueOption, 'Task pool');

  const runtime = createPoolRuntime<TaskWorker<TInput, TResult>, TInput, TResult>({
    abortedWhileQueuedMessage: 'The queued task was aborted before execution started.',
    clearMessage: 'The task pool queue was cleared.',
    concurrency,
    createWorkers(poolConcurrency) {
      const workers: Array<TaskWorker<TInput, TResult>> = [];
      const names = createWorkerNames(workerOptions.name, poolConcurrency);

      try {
        for (let index = 0; index < poolConcurrency; index++) {
          workers.push(
            createTaskWorker(source, {
              ...workerOptions,
              name: names[index],
            })
          );
        }
      } catch (error) {
        for (const worker of workers) {
          worker.terminate();
        }
        throw error;
      }

      return workers;
    },
    queueFullMessage:
      'The task pool queue is full. Increase maxQueue, wait for pending tasks, or raise pool concurrency.',
    terminatedMessage: 'The task pool was terminated.',
    workerTerminatedMessage: 'The task pool has already been terminated.',
    runWorker(worker, job, runOptions) {
      return worker.run(job, runOptions);
    },
    maxQueue,
  });

  return {
    get state(): TaskWorkerState {
      return runtime.state;
    },
    get busy(): boolean {
      return runtime.busy;
    },
    get concurrency(): number {
      return runtime.concurrency;
    },
    get pending(): number {
      return runtime.pending;
    },
    get size(): number {
      return runtime.size;
    },
    get paused(): boolean {
      return runtime.paused;
    },
    get metrics(): PoolMetrics {
      return runtime.metrics;
    },
    run(input: TInput, runOptions?: TaskRunOptions): Promise<TResult> {
      return runtime.enqueue(input, runOptions);
    },
    clear(): void {
      runtime.clear();
    },
    pause(): void {
      runtime.pause();
    },
    resume(): void {
      runtime.resume();
    },
    onIdle(): Promise<void> {
      return runtime.onIdle();
    },
    terminate(): void {
      runtime.terminate();
    },
  };
}

/**
 * Creates a reusable pool of RPC workers with bounded concurrency and FIFO queueing.
 *
 * @example
 * ```ts
 * import { createRpcPool } from '@bquery/bquery/concurrency';
 *
 * const pool = createRpcPool(
 *   {
 *     sum: ({ values }: { values: number[] }) => values.reduce((total, value) => total + value, 0),
 *   },
 *   { concurrency: 2, maxQueue: 8 }
 * );
 *
 * const total = await pool.call('sum', { values: [1, 2, 3] });
 * pool.terminate();
 * ```
 */
export function createRpcPool<TRoutes extends WorkerRpcHandlers>(
  source: WorkerRpcSource<TRoutes>,
  options: CreateRpcPoolOptions = {}
): RpcPool<TRoutes> {
  const { concurrency: concurrencyOption, maxQueue: maxQueueOption, ...workerOptions } = options;
  const concurrency = normalizeConcurrency(concurrencyOption, 'RPC pool');
  const maxQueue = normalizeMaxQueue(maxQueueOption, 'RPC pool');

  type RpcJob = {
    input: unknown;
    method: keyof TRoutes & string;
  };

  const runtime = createPoolRuntime<RpcWorker<TRoutes>, RpcJob, unknown>({
    abortedWhileQueuedMessage: 'The queued RPC call was aborted before execution started.',
    clearMessage: 'The RPC pool queue was cleared.',
    concurrency,
    createWorkers(poolConcurrency) {
      const workers: Array<RpcWorker<TRoutes>> = [];
      const names = createWorkerNames(workerOptions.name, poolConcurrency);

      try {
        for (let index = 0; index < poolConcurrency; index++) {
          workers.push(
            createRpcWorker(source, {
              ...workerOptions,
              name: names[index],
            })
          );
        }
      } catch (error) {
        for (const worker of workers) {
          worker.terminate();
        }
        throw error;
      }

      return workers;
    },
    queueFullMessage:
      'The RPC pool queue is full. Increase maxQueue, wait for pending calls, or raise pool concurrency.',
    terminatedMessage: 'The RPC pool was terminated.',
    workerTerminatedMessage: 'The RPC pool has already been terminated.',
    runWorker(worker, job, runOptions) {
      return worker.call(job.method, job.input as never, runOptions);
    },
    maxQueue,
  });

  return {
    get state(): TaskWorkerState {
      return runtime.state;
    },
    get busy(): boolean {
      return runtime.busy;
    },
    get concurrency(): number {
      return runtime.concurrency;
    },
    get pending(): number {
      return runtime.pending;
    },
    get size(): number {
      return runtime.size;
    },
    get paused(): boolean {
      return runtime.paused;
    },
    get metrics(): PoolMetrics {
      return runtime.metrics;
    },
    call<TMethod extends keyof TRoutes & string>(
      method: TMethod,
      input: Parameters<TRoutes[TMethod]>[0],
      runOptions?: TaskRunOptions
    ): Promise<Awaited<ReturnType<TRoutes[TMethod]>>> {
      return runtime.enqueue({ input, method }, runOptions) as Promise<
        Awaited<ReturnType<TRoutes[TMethod]>>
      >;
    },
    clear(): void {
      runtime.clear();
    },
    pause(): void {
      runtime.pause();
    },
    resume(): void {
      runtime.resume();
    },
    onIdle(): Promise<void> {
      return runtime.onIdle();
    },
    terminate(): void {
      runtime.terminate();
    },
  };
}
